import type { LifeEntity } from "./universal-life-runtime";
import type { EntityContinuity, LifeGoal, LifeGoalKind } from "./continuity";

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

interface Candidate {
  kind: LifeGoalKind;
  score: number;
  reason: string;
}

export class SovereignMind {
  reconcile(entity: LifeEntity, continuity: EntityContinuity, nowSeconds: number): EntityContinuity {
    const active = continuity.goals.find(goal => goal.status === "active");
    if (active) {
      active.updatedAt = nowSeconds;
      active.progress = clamp01(active.progress + this.progressRate(entity, active.kind));
      if (active.progress >= 1) active.status = "completed";
    }

    if (!continuity.goals.some(goal => goal.status === "active")) {
      continuity.goals.push(this.chooseGoal(entity, nowSeconds));
    }

    continuity.goals = continuity.goals.slice(-8);
    continuity.lastMindAt = nowSeconds;
    return continuity;
  }

  private chooseGoal(entity: LifeEntity, nowSeconds: number): LifeGoal {
    const d = entity.drives;
    const p = entity.personality;
    const candidates: Candidate[] = [
      { kind: "survive", score: d.safety * (0.7 + p.caution * 0.3), reason: "safety pressure" },
      { kind: "recover", score: d.fatigue * (0.6 + p.discipline * 0.2), reason: "fatigue pressure" },
      { kind: "explore", score: d.curiosity * (0.55 + p.curiosity * 0.45), reason: "curiosity pressure" },
      { kind: "bond", score: d.social * (0.5 + p.sociability * 0.5), reason: "social attachment" },
      { kind: "work", score: d.purpose * (0.5 + p.discipline * 0.5), reason: "purpose and discipline" },
      { kind: "protect", score: d.safety * (0.35 + p.empathy * 0.35 + p.territoriality * 0.3), reason: "protective pressure" },
      { kind: "hunt", score: d.hunger * (0.4 + p.aggression * 0.4 + p.courage * 0.2), reason: "hunger and hunting disposition" },
      { kind: "travel", score: d.curiosity * (0.35 + p.creativity * 0.35), reason: "novelty seeking" },
      { kind: "learn", score: entity.profile.learningRate * (0.45 + p.curiosity * 0.4), reason: "learning potential" },
    ];
    candidates.sort((a, b) => b.score - a.score || a.kind.localeCompare(b.kind));
    const best = candidates[0];
    return {
      id: `${entity.id}:${best.kind}:${entity.decisionCount}:${Math.floor(nowSeconds)}`,
      kind: best.kind,
      priority: clamp01(best.score),
      createdAt: nowSeconds,
      updatedAt: nowSeconds,
      progress: 0,
      status: "active",
      reason: best.reason,
    };
  }

  private progressRate(entity: LifeEntity, kind: LifeGoalKind): number {
    const actionMatch =
      (kind === "recover" && entity.action === "rest") ||
      (kind === "explore" && (entity.action === "explore" || entity.action === "investigate")) ||
      (kind === "bond" && (entity.action === "socialize" || entity.action === "follow")) ||
      (kind === "work" && entity.action === "work") ||
      (kind === "protect" && entity.action === "protect") ||
      (kind === "hunt" && entity.action === "hunt") ||
      (kind === "travel" && entity.action === "travel");
    return actionMatch ? 0.04 : 0.006;
  }
}
